/**
 * TeamAlignmentBanner.jsx
 *
 * Magical Leader Flow · Phase 4 — glowing notification + live "today's
 * transmission has dropped" toast.
 *
 * Polls /api/team-alignment/teams/mine/unwatched every 25s. If the
 * user is a member of any team that has a published, unwatched daily
 * transmission, surfaces a small premium pill at the top of HomePage
 * that gently pulses gold.
 *
 * NEW · live nudge: the first time we see a fresh alignment id (one
 * we haven't already toasted in this browser), we fire a sonner toast
 * "Today's transmission has dropped · step in." A one-tap action takes
 * the member straight into the journey. The alignment id is tracked in
 * localStorage so the toast fires once per publish per device, even
 * across reloads.
 *
 * Tapping the banner navigates straight to the team-alignment hub for
 * the freshest team. Silent otherwise — no API calls if the user is
 * unauthenticated or has no teams.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
// Tighter than the original 60s — closer to "live" without hammering
// the server. Still well within sane mobile-data budgets.
const POLL_MS = 25_000;
const TOASTED_KEY = "reclaim_team_alignment_toasted_v1";

function loadToastedIds() {
  try {
    const raw = localStorage.getItem(TOASTED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.slice(-25) : []);
  } catch { return new Set(); }
}

function persistToastedIds(set) {
  try {
    // Keep the last 25 ids — plenty for de-duping across reloads and
    // safely capped against localStorage bloat.
    localStorage.setItem(TOASTED_KEY, JSON.stringify(Array.from(set).slice(-25)));
  } catch { /* private mode */ }
}

export default function TeamAlignmentBanner() {
  const navigate = useNavigate();
  const [unwatched, setUnwatched] = useState(null);
  // Persist across renders without re-firing toasts when the banner
  // re-mounts (StrictMode safety).
  const toastedRef = useRef(loadToastedIds());
  const firstFetchRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const token = localStorage.getItem("reclaim_token");
    if (!token) return undefined;

    const tick = async () => {
      try {
        const r = await axios.get(
          `${API}/api/team-alignment/teams/mine/unwatched`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setUnwatched(r.data);

        // "Live drop" toast — only fire once per alignment id per
        // device. We skip the FIRST fetch so a user landing on the
        // page with an existing unwatched transmission still sees the
        // glowing banner, but isn't ambushed by a toast that fires
        // every page-load.
        const teams = r.data?.teams || [];
        if (teams.length > 0 && !firstFetchRef.current) {
          for (const t of teams) {
            const aid = t.alignment_id;
            if (!aid || toastedRef.current.has(aid)) continue;
            toastedRef.current.add(aid);
            persistToastedIds(toastedRef.current);
            const teamName = t.team_name || "your team";
            const focus = t.focus_phrase;
            toast(`Today's transmission has dropped · ${teamName}`, {
              description: focus ? `“${focus}”` : "Step into the field.",
              action: {
                label: "Step in",
                onClick: () => navigate(`/team-alignment/${t.team_id}`),
              },
              duration: 9000,
            });
          }
        }
        firstFetchRef.current = false;
      } catch {
        if (!cancelled) setUnwatched(null);
      }
    };
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [navigate]);

  if (!unwatched?.has_unwatched) return null;
  const first = (unwatched.teams || [])[0];
  const teamName = first?.team_name || "your team";
  const focus = first?.focus_phrase;
  const route = first?.team_id ? `/team-alignment/${first.team_id}` : "/team-alignment";

  return (
    <AnimatePresence>
      <motion.button
        key="ta-banner"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.6 }}
        onClick={() => navigate(route)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all"
        style={{
          background:
            "radial-gradient(ellipse at 0% 50%, rgba(215,187,122,0.22) 0%, rgba(7,13,11,0.85) 70%)",
          border: "1px solid rgba(215,187,122,0.45)",
          boxShadow: "0 0 24px rgba(215,187,122,0.12)",
        }}
        data-testid="team-alignment-banner"
      >
        <motion.div
          animate={{
            opacity: [0.55, 1, 0.55],
            scale: [1, 1.18, 1],
            boxShadow: [
              "0 0 4px rgba(215,187,122,0.4)",
              "0 0 14px rgba(215,187,122,0.95)",
              "0 0 4px rgba(215,187,122,0.4)",
            ],
          }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ background: "#E8C77A" }}
          data-testid="team-alignment-banner-dot"
        />
        <div className="flex-1 min-w-0">
          <p
            className="uppercase tracking-[0.28em]"
            style={{
              color: "rgba(215,187,122,0.85)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.56rem",
              fontWeight: 500,
            }}
          >
            New transmission · {teamName}
          </p>
          <p
            className="italic truncate"
            style={{
              color: "rgba(243,237,221,0.95)",
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "1rem",
              lineHeight: 1.25,
            }}
            data-testid="team-alignment-banner-phrase"
          >
            {focus ? `“${focus}”` : "Step into today's alignment"}
          </p>
        </div>
        <Sparkles
          size={14}
          strokeWidth={1.6}
          style={{ color: "rgba(215,187,122,0.7)", flexShrink: 0 }}
        />
      </motion.button>
    </AnimatePresence>
  );
}
